/**
 * MockSellerFlowApiClient — drop-in replacement for sellerFlowApiClient.
 *
 * Activated when import.meta.env.VITE_ENABLE_MOCK_GENERATION === 'true'.
 * Returns canned snake_case payloads matching the real backend contract,
 * so all the existing sellerFlowMappers normalize them correctly.
 *
 * Seed business: Hearth — Wood-Fired Kitchen & Bar (matches mockStudioData.js).
 *
 * Time-based progression:
 *   - generateWorkspaceCreative() returns status="processing" for ~3.5s,
 *     then transitions to "ready" so the filmstrip CinematicLoader plays.
 *   - billing.start → billing.confirm transitions are synchronous.
 *
 * NOT a complete reimplementation of every backend nuance — just enough
 * for the seller workflow demo to run end-to-end.
 */

import { MOCK_JOB_ID, MOCK_VIDEO_URL, MOCK_BUSINESS, MOCK_PHOTOS } from '@/services/mockStudioData';

// ── Stable demo identifiers ───────────────────────────────────────────────
const ADV_ID = 'adv-hearth-001';
const WORKSPACE_ID = 'ws-hearth-001';
const PROPOSAL_ID = 'prop-hearth-001';
const AGREEMENT_ID = 'agr-hearth-001';
const CAMPAIGN_ID = 'camp-hearth-001';
const CREATIVE_BINDING_ID = 'bind-hearth-001';
const CREATIVE_ID = 'crt-hearth-001';

const NOW = () => new Date().toISOString();
const PAST = (minutes) => new Date(Date.now() - minutes * 60_000).toISOString();
const FUTURE = (days) => new Date(Date.now() + days * 24 * 60 * 60_000).toISOString();
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ── In-memory state ───────────────────────────────────────────────────────
function createInitialState() {
  return {
    workspaces: {},          // workspaceId -> workspace record
    businessProfiles: {},    // workspaceId -> profile
    creatives: {},           // workspaceId -> creative bindings array
    creativeStartedAt: {},   // bindingId -> Date.now() when generate was called
    proposals: [],           // global proposals list (single-deal demo)
    agreements: {},          // proposalId -> agreement
    campaigns: {},           // workspaceId -> campaign
    reportSummaries: {},     // workspaceId -> summary
    snapshots: {},           // campaignId -> latest snapshot
    activity: [],
    selectedBindingByWorkspace: {},
  };
}

let STATE = createInitialState();

// ── Optional pre-seed for the late-scenes capture ────────────────────────
// When VITE_PRESEED_LATE === 'true', we bootstrap the workspace already in
// the "accepted, billing confirmed, campaign live" state so the late-scenes
// capture spec can deep-link to /proposal, /close, /launch, /live without
// driving the entire flow each time. This is OFF by default — the regular
// demo capture wants to start with zero proposals.
function maybePreseedLateState() {
  try {
    const flag = (typeof import.meta !== 'undefined') && import.meta.env && import.meta.env.VITE_PRESEED_LATE === 'true';
    if (!flag) return;
  } catch (_) { return; }
  // Workspace + business + completed creative.
  ensureSeedWorkspace();
  STATE.creatives[WORKSPACE_ID] = [seedCompletedCreative()];
  STATE.selectedBindingByWorkspace[WORKSPACE_ID] = CREATIVE_BINDING_ID;
  // Sent proposal — UI shows Copy share link + Update buyer response so the
  // late capture spec can interact with both. Scene 13 transitions it to
  // accepted via the actual UI click; the mock's recordProposalResponse
  // handler updates STATE.proposals on the click.
  const p = seedProposal();
  p.status = 'sent';
  p.sent_at = NOW();
  p.public_url = 'https://app.cornercast.tv/p/p2cc-hearth-2week';
  p.active_share_link = {
    id: 'share-preseed',
    token: 'p2cc-hearth-2week',
    public_url: p.public_url,
    publicUrl: p.public_url,
    created_at: NOW(),
    expires_at: FUTURE(30),
  };
  STATE.proposals = [p];
  // Pre-confirmed billing + live campaign so /close, /launch, /live render
  // as ready when the spec deep-links to them.
  const a = seedAgreement('payment_confirmed');
  STATE.agreements[PROPOSAL_ID] = a;
  STATE.campaigns[WORKSPACE_ID] = seedCampaign();
  STATE.reportSummaries[WORKSPACE_ID] = seedReportSummary();
  STATE.snapshots[CAMPAIGN_ID] = seedSnapshot();
}

// ── Hearth seed business profile ──────────────────────────────────────────
// PitchKit's `hasSeededApiMedia` reads `profile.canonicalProfileJson.photos`,
// which the mapper takes from `canonical_profile_json`. If that field is
// missing, the configure phase shows a loading state forever — and because
// the bootstrap response then triggers state cycles, React hits "Maximum
// update depth exceeded". So we populate canonical_profile_json with photos
// + reviews + logo_data_url to short-circuit the loading guard.
function seedCanonicalProfile() {
  return {
    name: MOCK_BUSINESS.name,
    address: MOCK_BUSINESS.address,
    city: 'Denver',
    state: 'CO',
    stars: MOCK_BUSINESS.rating,
    review_count: MOCK_BUSINESS.reviews_count,
    photos: MOCK_PHOTOS.map((p) => ({
      photo_id: p.photo_id,
      photo_name_id: p.photo_name_id,
      data_url: p.data_url,
      url: p.data_url,
      caption: p.caption,
      selected: p.selected !== false,
    })),
    reviews: [
      { review_id: 'mr-001', author: 'Sarah M.', rating: 5, text: 'The wood-fired flavors are incredible.', selected: true },
      { review_id: 'mr-002', author: 'Daniel K.', rating: 5, text: 'Best dining experience in Denver.', selected: true },
      { review_id: 'mr-003', author: 'Priya R.', rating: 5, text: 'The seasonal tasting menu was a revelation.', selected: false },
    ],
    brand_identity: {
      category: MOCK_BUSINESS.category,
      primary_category: MOCK_BUSINESS.category,
      vertical: 'Restaurant · Bar',
      logo_data_url: '/.mock/logo.png',
    },
    additional_info: null,
  };
}

function seedHearthBusinessProfile() {
  return {
    id: ADV_ID,
    advertiser_id: ADV_ID,
    workspace_id: WORKSPACE_ID,
    name: MOCK_BUSINESS.name,
    address: MOCK_BUSINESS.address,
    place_id: MOCK_BUSINESS.placeId,
    website_url: MOCK_BUSINESS.website,
    phone: MOCK_BUSINESS.phone,
    location_geo: { lat: 39.7615, lng: -105.0073 },
    brand_identity: {
      category: MOCK_BUSINESS.category,
      primary_category: MOCK_BUSINESS.category,
      vertical: 'Restaurant · Bar',
    },
    photos: MOCK_PHOTOS.map((p, i) => ({
      photo_id: p.photo_id,
      url: p.data_url,
      caption: p.caption,
      sort_order: i,
    })),
    logo_url: '/.mock/logo.png',
    review_count: MOCK_BUSINESS.reviews_count,
    stars: MOCK_BUSINESS.rating,
    rating: MOCK_BUSINESS.rating,
    reviews: MOCK_BUSINESS.reviews_count,
    enrichment_status: 'ready',
    canonical_profile_json: seedCanonicalProfile(),
    source_refs_json: { name: MOCK_BUSINESS.name, address: MOCK_BUSINESS.address, place_id: MOCK_BUSINESS.placeId },
  };
}

function seedWorkspace() {
  return {
    id: WORKSPACE_ID,
    workspace_id: WORKSPACE_ID,
    advertiser_id: ADV_ID,
    organization_id: 'org-iheart-demo',
    status: 'active',
    selected_creative_binding_id: STATE.selectedBindingByWorkspace[WORKSPACE_ID] || null,
    business: seedHearthBusinessProfile(),
    advertiser: {
      id: ADV_ID,
      name: MOCK_BUSINESS.name,
    },
    created_at: PAST(60),
    updated_at: NOW(),
  };
}

function ensureSeedWorkspace() {
  if (!STATE.workspaces[WORKSPACE_ID]) {
    STATE.workspaces[WORKSPACE_ID] = seedWorkspace();
    STATE.businessProfiles[WORKSPACE_ID] = seedHearthBusinessProfile();
    STATE.creatives[WORKSPACE_ID] = [];
  }
  return STATE.workspaces[WORKSPACE_ID];
}

function seedCompletedCreative() {
  return {
    binding_id: CREATIVE_BINDING_ID,
    creative_id: CREATIVE_ID,
    workspace_id: WORKSPACE_ID,
    advertiser_id: ADV_ID,
    name: 'Hearth — Spec Creative',
    display_name: 'Spec creative · 15s',
    status: 'ready',
    state: 'ready',
    aspect_ratio: '16:9',
    duration_seconds: 15,
    duration: 15,
    job_id: MOCK_JOB_ID,
    preview_video_url: MOCK_VIDEO_URL,
    video_url: MOCK_VIDEO_URL,
    thumbnail_url: `/.mock/${MOCK_JOB_ID}.jpg`,
    poster_url: `/.mock/${MOCK_JOB_ID}.jpg`,
    is_selected: true,
    created_at: PAST(10),
    updated_at: NOW(),
    brief: {
      purpose: 'awareness',
      tone: 'warm',
      channel: 'ctv',
      notes: 'Wood-fired kitchen with seasonal menu. Highlight chef\'s craft and warm dining-room atmosphere.',
    },
    photos_used: MOCK_PHOTOS.slice(0, 4).map((p) => p.photo_id),
  };
}

function processingCreative(elapsedMs, totalMs = 3500) {
  const pct = Math.min(99, Math.round((elapsedMs / totalMs) * 100));
  return {
    binding_id: CREATIVE_BINDING_ID,
    creative_id: CREATIVE_ID,
    workspace_id: WORKSPACE_ID,
    advertiser_id: ADV_ID,
    name: 'Hearth — Spec Creative',
    display_name: 'Spec creative · 15s',
    status: 'processing',
    state: 'processing',
    aspect_ratio: '16:9',
    duration_seconds: 15,
    job_id: MOCK_JOB_ID,
    progress_pct: pct,
    progress: pct,
    is_selected: false,
    created_at: NOW(),
    updated_at: NOW(),
  };
}

function seedProposal() {
  return {
    id: PROPOSAL_ID,
    proposal_id: PROPOSAL_ID,
    workspace_id: WORKSPACE_ID,
    advertiser_id: ADV_ID,
    version: 1,
    status: 'draft',
    package_name: '2-Week Starter',
    pitch_message: '',
    creative_binding_id: CREATIVE_BINDING_ID,
    selected_creative_binding_id: CREATIVE_BINDING_ID,
    selected_package: {
      id: 'starter-2w',
      name: '2-Week Starter',
      duration_label: '2 weeks',
      duration_days: 14,
      pricing: {
        sellPrice: 3750,
        platformCost: 1500,
        sellerEarnings: 2250,
        marginPct: 0.6,
        markupPct: 1.5,
        tierId: 'standard',
      },
      flight: { label: '2 weeks', days: 14, start_date: NOW(), end_date: FUTURE(14) },
      target_area: { label: 'Lower Denver · 2 mi radius' },
      reach_estimate: 450_000,
      cpm: 15,
    },
    target_area: { label: 'Lower Denver · 2 mi radius' },
    flight: { label: '2 weeks', days: 14, start_date: NOW(), end_date: FUTURE(14) },
    pricing_summary: {
      sellPrice: 3750,
      platformCost: 1500,
      sellerEarnings: 2250,
      marginPct: 0.6,
    },
    delivery_estimate: {
      reach: 450_000,
      impressions: 250_000,
      cpm: 15,
    },
    reach_estimate: 450_000,
    cpm: 15,
    contact_name: 'Hearth Owner',
    contact_email: 'owner@hearthdenver.com',
    public_url: null,
    active_share_link: null,
    sent_at: null,
    accepted_at: null,
    created_at: PAST(5),
    updated_at: NOW(),
  };
}

function ensureSeedProposal() {
  if (STATE.proposals.length === 0) {
    STATE.proposals.push(seedProposal());
  }
  return STATE.proposals[0];
}

function seedAgreement(paymentStatus = 'not_started') {
  return {
    id: AGREEMENT_ID,
    agreement_id: AGREEMENT_ID,
    proposal_id: PROPOSAL_ID,
    workspace_id: WORKSPACE_ID,
    status: paymentStatus === 'payment_confirmed' ? 'active' : 'pending',
    payment_method_status: paymentStatus,
    billing_status: paymentStatus,
    billing_link: paymentStatus !== 'not_started' ? `https://cornercast.tv/pay/${PROPOSAL_ID}` : null,
    payment_method_label: paymentStatus === 'payment_confirmed' ? 'Visa •••• 4242' : null,
    created_at: PAST(2),
    updated_at: NOW(),
  };
}

function seedLaunchReadiness({ ready = false, hasProposal = false, billingConfirmed = false, creativeSelected = false } = {}) {
  const blockers = [];
  if (!hasProposal) blockers.push({ code: 'proposal_missing', message: 'Proposal is missing' });
  if (hasProposal && !billingConfirmed) blockers.push({ code: 'billing_unconfirmed', message: 'Billing is not confirmed' });
  if (!creativeSelected) blockers.push({ code: 'selected_creative_missing', message: 'No creative is selected' });
  return {
    workspace_id: WORKSPACE_ID,
    status: ready ? 'ready' : 'pending',
    ready,
    blockers,
    current_proposal: hasProposal ? { id: PROPOSAL_ID, version: 1, status: ready ? 'accepted' : 'draft' } : null,
    current_agreement: { billing_status: billingConfirmed ? 'payment_confirmed' : 'not_started' },
    launch_selected_creative_binding_id: creativeSelected ? CREATIVE_BINDING_ID : null,
    recommended_next_step_code: ready ? 'launch_campaign' : 'continue_setup',
    recommended_next_step_text: ready ? 'Launch the campaign' : 'Continue setting up the campaign',
  };
}

function seedCampaign() {
  return {
    id: CAMPAIGN_ID,
    campaign_id: CAMPAIGN_ID,
    workspace_id: WORKSPACE_ID,
    advertiser_id: ADV_ID,
    proposal_id: PROPOSAL_ID,
    proposal_version: 1,
    proposalVersion: 1,
    status: 'live',
    state: 'live',
    launched_at: PAST(60 * 24 * 5), // 5 days ago
    start_date: PAST(60 * 24 * 5),
    end_date: FUTURE(9),
    budget_cap: 3750,
    spend: 1278,
    impressions: 126_410,
    households_reached: 48_230,
    cpm: 15,
    days_remaining: 9,
    name: '2-Week Starter — Hearth',
    package_name: '2-Week Starter',
  };
}

function seedReportSummary() {
  return {
    campaign_id: CAMPAIGN_ID,
    workspace_id: WORKSPACE_ID,
    delivery_status: 'on_pace',
    pacing: 'on_pace',
    health: 'healthy_pacing',
    summary_text: 'Hearth is in delivery and pacing on plan with strong reach across the lunch and dinner dayparts.',
    headline_metrics: {
      impressions: 126_410,
      reach: 48_230,
      spend: 1278,
      cpm: 15,
    },
    warnings: [],
    last_updated: NOW(),
  };
}

function seedSnapshot() {
  return {
    id: 'snap-hearth-latest',
    snapshot_id: 'snap-hearth-latest',
    campaign_id: CAMPAIGN_ID,
    captured_at: NOW(),
    metrics_json: {
      households_reached: 48_230,
      impressions: 126_410,
      spend: 1278,
      cpm: 15,
      budget_cap: 3750,
      days_remaining: 9,
      start_date: PAST(60 * 24 * 5),
      end_date: FUTURE(9),
      frequency: 2.62,
      completion_rate: 0.95,
    },
  };
}

// ── Seller home (queue + summary) ────────────────────────────────────────
function seedSellerHomeSummary() {
  return {
    total_active_workspaces: 6,
    needs_attention_count: 2,
    ready_to_launch_count: 1,
    live_campaigns_count: 2,
    awaiting_buyer_count: 3,
    awaiting_billing_count: 1,
    reporting_pending_count: 0,
    stage_buckets: [
      { stage: 'discovery', count: 1 },
      { stage: 'creative_ready', count: 1 },
      { stage: 'offer_in_progress', count: 1 },
      { stage: 'awaiting_buyer', count: 1 },
      { stage: 'accepted_pending_billing', count: 1 },
      { stage: 'live', count: 1 },
    ],
  };
}

function seedSellerHomeQueue() {
  const baseLeads = [
    {
      workspace_id: WORKSPACE_ID,
      advertiser_id: ADV_ID,
      advertiser_name: MOCK_BUSINESS.name,
      stage: 'creative_ready',
      status_code: 'top_priority',
      status_label: 'Top priority',
      recommended_next_step_code: 'open_workspace',
      recommended_next_step_text: 'Open workspace and start a fresh pitch',
      blockers: [{ code: 'creative_missing', message: 'No creative on file yet' }],
      needs_attention: true,
      stale: true,
      stale_reason: 'No activity in 8 days',
      last_activity_at: PAST(60 * 24 * 8),
      updated_at: PAST(60 * 24 * 8),
      business: {
        name: MOCK_BUSINESS.name,
        address: MOCK_BUSINESS.address,
        city: 'Denver',
        state: 'CO',
        stars: MOCK_BUSINESS.rating,
        review_count: MOCK_BUSINESS.reviews_count,
        brand_identity: { category: MOCK_BUSINESS.category, vertical: 'Restaurant · Bar' },
      },
      current_proposal: null,
      current_agreement: null,
      current_campaign: null,
      latest_reporting_summary: null,
    },
    {
      workspace_id: 'ws-casa-002',
      advertiser_id: 'adv-casa-002',
      advertiser_name: 'Casa Forneria São Paulo',
      stage: 'awaiting_buyer',
      status_label: 'Awaiting buyer',
      status_code: 'awaiting_buyer',
      recommended_next_step_text: 'Send proposal v2 with refreshed creative',
      blockers: [{ code: 'buyer_review', message: 'Buyer reviewed proposal v2 — no response in 4 days' }],
      needs_attention: false,
      stale: false,
      last_activity_at: PAST(60 * 24 * 1),
      updated_at: PAST(60 * 24 * 1),
      business: {
        name: 'Casa Forneria São Paulo',
        address: '742 Rua Augusta, São Paulo, SP',
        stars: 4.5,
        review_count: 188,
        brand_identity: { category: 'Italian Restaurant', vertical: 'Restaurant' },
      },
    },
    {
      workspace_id: 'ws-pacheco-003',
      advertiser_id: 'adv-pacheco-003',
      advertiser_name: 'Drogaria Pacheco',
      stage: 'discovery',
      status_label: 'New opportunity',
      status_code: 'new',
      recommended_next_step_text: 'Build pitch creative',
      blockers: [],
      needs_attention: false,
      stale: false,
      last_activity_at: PAST(60 * 5),
      updated_at: PAST(60 * 5),
      business: {
        name: 'Drogaria Pacheco',
        address: '120 Av. Paulista, São Paulo, SP',
        brand_identity: { category: 'Pharmacy', vertical: 'Retail' },
      },
    },
    {
      workspace_id: 'ws-paodequeijo-004',
      advertiser_id: 'adv-paodequeijo-004',
      advertiser_name: 'Casa do Pão de Queijo',
      stage: 'live',
      status_label: 'Live campaign',
      status_code: 'live',
      recommended_next_step_text: 'Share mid-flight metrics with buyer',
      blockers: [],
      needs_attention: false,
      stale: false,
      last_activity_at: PAST(60 * 2),
      updated_at: PAST(60 * 2),
      business: {
        name: 'Casa do Pão de Queijo',
        address: '88 Av. Brigadeiro, São Paulo, SP',
        brand_identity: { category: 'Café', vertical: 'Restaurant' },
      },
    },
    {
      workspace_id: 'ws-tenda-005',
      advertiser_id: 'adv-tenda-005',
      advertiser_name: 'Tenda Atacado',
      stage: 'offer_in_progress',
      status_label: 'Stalled',
      status_code: 'stalled',
      recommended_next_step_text: 'Try a fresh angle — buyer ghosted',
      blockers: [{ code: 'buyer_ghost', message: 'Buyer ghosted after offer' }],
      needs_attention: true,
      stale: true,
      stale_reason: '11 days since last activity',
      last_activity_at: PAST(60 * 24 * 11),
      updated_at: PAST(60 * 24 * 11),
      business: {
        name: 'Tenda Atacado',
        address: '500 Av. Atacadão, Rio de Janeiro, RJ',
        brand_identity: { category: 'Wholesale', vertical: 'Retail' },
      },
    },
    {
      workspace_id: 'ws-shell-006',
      advertiser_id: 'adv-shell-006',
      advertiser_name: 'Posto Shell BR-101',
      stage: 'discovery',
      status_label: 'New opportunity',
      status_code: 'new',
      recommended_next_step_text: 'Hot lead, peak season',
      blockers: [],
      needs_attention: false,
      stale: false,
      last_activity_at: PAST(60 * 1),
      updated_at: PAST(60 * 1),
      business: {
        name: 'Posto Shell BR-101',
        address: 'BR-101 KM 320, Niterói, RJ',
        brand_identity: { category: 'Gas Station', vertical: 'Auto' },
      },
    },
    // Names below match the existing 02-leads-pipeline.spec.js selectors
    // so the lead-pipeline screencast keeps working with the mock client.
    {
      workspace_id: 'ws-kfire',
      advertiser_id: 'adv-kfire',
      advertiser_name: 'KFire Korean BBQ',
      stage: 'awaiting_buyer',
      status_label: 'Review',
      status_code: 'review',
      recommended_next_step_text: 'Approve creative and send proposal',
      blockers: [],
      needs_attention: false,
      stale: false,
      last_activity_at: PAST(60 * 4),
      updated_at: PAST(60 * 4),
      business: {
        name: 'KFire Korean BBQ',
        address: '1455 Larimer St, Denver, CO',
        stars: 4.6, review_count: 412,
        brand_identity: { category: 'Korean BBQ', vertical: 'Restaurant' },
      },
    },
    {
      workspace_id: 'ws-audi',
      advertiser_id: 'adv-audi',
      advertiser_name: 'Audi Denver',
      stage: 'awaiting_buyer',
      status_label: 'Review',
      status_code: 'review',
      recommended_next_step_text: 'Send proposal',
      blockers: [],
      needs_attention: false,
      stale: false,
      last_activity_at: PAST(60 * 12),
      updated_at: PAST(60 * 12),
      business: {
        name: 'Audi Denver',
        address: '6160 S Broadway, Centennial, CO',
        stars: 4.4, review_count: 287,
        brand_identity: { category: 'Auto Dealership', vertical: 'Automotive' },
      },
    },
  ];
  return baseLeads;
}

// ── Mock client ───────────────────────────────────────────────────────────
class MockSellerFlowApiClient {
  constructor() {
    // Pre-seed everything
    ensureSeedWorkspace();
  }

  // Reset for tests
  __reset() {
    STATE = createInitialState();
    ensureSeedWorkspace();
  }

  // Seller home
  async getSellerHomeSummary() {
    await wait(120);
    return seedSellerHomeSummary();
  }

  async getSellerHomeQueue(/* filters */) {
    await wait(150);
    return { items: seedSellerHomeQueue() };
  }

  // Workspaces
  async ensureWorkspace(advertiserId, originJson = null) {
    await wait(120);
    ensureSeedWorkspace();
    return STATE.workspaces[WORKSPACE_ID];
  }

  async getActiveWorkspace(advertiserId) {
    await wait(80);
    return ensureSeedWorkspace();
  }

  async getWorkspace(workspaceId) {
    await wait(80);
    return ensureSeedWorkspace();
  }

  async bootstrapPitchKit(workspaceId, options = {}) {
    await wait(140);
    const profile = STATE.businessProfiles[WORKSPACE_ID] || seedHearthBusinessProfile();
    // PitchKit (after the scope phase) calls bootstrapPitchKitForBusiness which
    // pipes this response through pitchBootstrapFromBackend. That mapper expects
    // a flattened backend shape including binding_id, job_id, state, business
    // (with photos/reviews), and video_config — otherwise the configure phase
    // can't render and gets stuck in the loading state.
    return {
      workspace_id: WORKSPACE_ID,
      advertiser_id: ADV_ID,
      binding_id: CREATIVE_BINDING_ID,
      job_id: MOCK_JOB_ID,
      business_snapshot_id: 'snap-business-hearth-001',
      creative_name: 'Hearth — Spec Pitch',
      reused_existing: false,
      state: 'draft',
      selected_on_workspace: false,
      // Creative-shape fields read directly off the response by creativeFromBackend
      thumbnail_url: `/api/v1/videos/${MOCK_JOB_ID}/thumbnail`,
      preview_url: '',
      video_url: '',
      share_url: '',
      aspect_ratio: '16:9',
      format: 'ctv_16_9',
      objective: 'awareness',
      tone: 'warm',
      scope: options?.scope || null,
      duration: 15,
      project_folder: null,
      error_detail: null,
      generation_request_json: null,
      business: {
        name: profile.name,
        address: profile.address,
        place_id: profile.place_id,
        website_url: profile.website_url,
        logo_url: profile.logo_url,
        logo_data_url: profile.logo_url,
        stars: profile.stars,
        review_count: profile.review_count,
        brand_identity: profile.brand_identity,
        photos: MOCK_PHOTOS.map((p) => ({
          photo_id: p.photo_id,
          photo_name_id: p.photo_name_id,
          data_url: p.data_url,
          url: p.data_url,
          caption: p.caption,
          selected: p.selected !== false,
        })),
        reviews: [
          { review_id: 'mr-001', author: 'Sarah M.', rating: 5, text: 'The wood-fired flavors are incredible.', selected: true },
          { review_id: 'mr-002', author: 'Daniel K.', rating: 5, text: 'Best dining experience in Denver.', selected: true },
          { review_id: 'mr-003', author: 'Priya R.', rating: 5, text: 'The seasonal tasting menu was a revelation.', selected: false },
        ],
      },
      video_config: {
        aspect_ratio: '16:9',
        resolution: '1080p',
        length: 15,
        video_provider: 'cornercast_runway',
        script_mode: 'auto',
        logo_overlay_enabled: true,
        logo_overlay_position: 'bottom_right',
        logo_overlay_opacity: 0.85,
      },
      director: {
        business_data_url: '',
        script_url: '',
        video_config_url: '',
        campaign_brief_url: '',
        characters_url: '',
      },
      created_at: NOW(),
      updated_at: NOW(),
    };
  }

  // Business profile
  async getAdvertiserBusinessProfile(advertiserId) {
    await wait(80);
    return seedHearthBusinessProfile();
  }

  async getWorkspaceBusinessProfile(workspaceId) {
    await wait(80);
    return seedHearthBusinessProfile();
  }

  async enrichAdvertiserBusinessProfile(advertiserId) {
    await wait(120);
    return seedHearthBusinessProfile();
  }

  async enrichWorkspaceBusinessProfile(workspaceId) {
    await wait(120);
    return seedHearthBusinessProfile();
  }

  async getWorkspaceQueueSummary(workspaceId) {
    await wait(80);
    return { workspace_id: workspaceId, status: 'creative_ready' };
  }

  async getWorkspaceActivity(workspaceId) {
    await wait(80);
    return {
      items: [
        { id: 'act-1', type: 'workspace_created', label: 'Workspace created', occurred_at: PAST(60 * 24 * 8) },
        { id: 'act-2', type: 'profile_enriched', label: 'Business profile enriched from Google Places', occurred_at: PAST(60 * 24 * 8 - 5) },
      ],
    };
  }

  // Creatives
  async listAdvertiserCreatives(advertiserId, workspaceId = null) {
    await wait(80);
    return { items: STATE.creatives[WORKSPACE_ID] || [] };
  }

  async getWorkspaceCreatives(workspaceId) {
    await wait(60);
    return { items: STATE.creatives[WORKSPACE_ID] || [] };
  }

  async getWorkspaceCreative(workspaceId, bindingId) {
    await wait(50);
    const list = STATE.creatives[WORKSPACE_ID] || [];
    const found = list.find((c) => c.binding_id === bindingId);
    if (found) {
      // If still in the "processing" state, simulate progress based on
      // elapsed wall-clock time. After ~3.5s, transition to "ready".
      // Reading either `status` (camelCase) or `state` (snake) covers both.
      const isProcessing = found.status === 'processing' || found.state === 'processing';
      if (isProcessing) {
        const startedAt = STATE.creativeStartedAt[bindingId] || Date.now();
        const elapsed = Date.now() - startedAt;
        if (elapsed >= 3500) {
          const ready = seedCompletedCreative();
          ready.binding_id = bindingId;
          STATE.creatives[WORKSPACE_ID] = list.map((c) => (c.binding_id === bindingId ? ready : c));
          return ready;
        }
        const stillProcessing = processingCreative(elapsed);
        stillProcessing.binding_id = bindingId;
        return stillProcessing;
      }
      return found;
    }
    // The polling loop expects a non-null payload — returning null causes
    // creativeFromBackend to crash on item.thumbnail_url. If the binding
    // isn't in our state yet (race during the first poll right after
    // generateWorkspaceCreative), return a "still processing" snapshot so
    // the polling cycles cleanly.
    const fallback = processingCreative(0);
    fallback.binding_id = bindingId;
    return fallback;
  }

  async updateWorkspaceCreative(workspaceId, bindingId, payload) {
    await wait(60);
    const list = STATE.creatives[WORKSPACE_ID] || [];
    const updated = list.map((c) => (c.binding_id === bindingId ? { ...c, ...payload, updated_at: NOW() } : c));
    STATE.creatives[WORKSPACE_ID] = updated;
    return updated.find((c) => c.binding_id === bindingId);
  }

  async deleteWorkspaceCreative(workspaceId, bindingId) {
    await wait(60);
    const list = STATE.creatives[WORKSPACE_ID] || [];
    STATE.creatives[WORKSPACE_ID] = list.filter((c) => c.binding_id !== bindingId);
    return null;
  }

  async retryWorkspaceCreative(workspaceId, bindingId) {
    await wait(80);
    return this.generateWorkspaceCreative(workspaceId, {});
  }

  async generateWorkspaceCreative(workspaceId, payload) {
    await wait(120);
    const bindingId = `bind-${Date.now()}`;
    const processing = processingCreative(0);
    processing.binding_id = bindingId;
    processing.creative_id = `crt-${Date.now()}`;
    STATE.creatives[WORKSPACE_ID] = [...(STATE.creatives[WORKSPACE_ID] || []), processing];
    STATE.creativeStartedAt[bindingId] = Date.now();
    return processing;
  }

  async selectWorkspaceCreative(workspaceId, bindingId) {
    await wait(60);
    STATE.selectedBindingByWorkspace[WORKSPACE_ID] = bindingId;
    const ws = ensureSeedWorkspace();
    ws.selected_creative_binding_id = bindingId;
    const list = STATE.creatives[WORKSPACE_ID] || [];
    STATE.creatives[WORKSPACE_ID] = list.map((c) => ({ ...c, is_selected: c.binding_id === bindingId }));
    return { workspace_id: workspaceId, selected_creative_binding_id: bindingId };
  }

  // Offer config
  async getOfferConfig(workspaceId) {
    await wait(80);
    return {
      workspace_id: workspaceId,
      selected_package: seedProposal().selected_package,
      pricing_summary: seedProposal().pricing_summary,
      seller_economics: { sellerEarnings: 2250, marginPct: 0.6, markupPct: 1.5 },
      target_area: { label: 'Lower Denver · 2 mi radius' },
      flight: { label: '2 weeks', days: 14 },
      objective: 'awareness',
    };
  }

  async upsertOfferConfig(workspaceId, config) {
    await wait(100);
    return { workspace_id: workspaceId, ...config };
  }

  // Proposals
  // NOTE: do not auto-seed a proposal — the workspace must start with zero
  // proposals so the "Generate creative" CTA is the primary action. The
  // proposal is created on demand when the seller navigates to /proposal
  // (ProposalBuilder calls createProposal via ensureDraftProposalForBusiness).
  async listProposals(workspaceId) {
    await wait(80);
    return { items: STATE.proposals };
  }

  async createProposal(workspaceId, payload) {
    await wait(120);
    const p = seedProposal();
    if (payload?.package_name) p.package_name = payload.package_name;
    STATE.proposals = [p];
    return p;
  }

  async getProposal(proposalId) {
    await wait(60);
    if (STATE.proposals.length === 0) return null;
    return STATE.proposals.find((p) => p.id === proposalId) || STATE.proposals[0];
  }

  async patchProposal(proposalId, payload) {
    await wait(80);
    const p = STATE.proposals.find((x) => x.id === proposalId) || ensureSeedProposal();
    Object.assign(p, payload, { updated_at: NOW() });
    return p;
  }

  async shareProposal(proposalId, payload = {}) {
    await wait(140);
    const p = STATE.proposals.find((x) => x.id === proposalId) || ensureSeedProposal();
    const token = 'p2cc-hearth-2week';
    const publicUrl = `https://app.cornercast.tv/p/${token}`;
    p.public_url = publicUrl;
    p.active_share_link = {
      id: `share-${Date.now()}`,
      token,
      public_url: publicUrl,
      publicUrl,
      created_at: NOW(),
      expires_at: FUTURE(30),
    };
    p.publicUrl = publicUrl;
    return p;
  }

  async sendProposalWithPayload(proposalId, payload = {}) {
    await wait(200);
    const p = await this.shareProposal(proposalId, payload);
    p.status = 'sent';
    p.sent_at = NOW();
    return p;
  }

  async sendProposal(proposalId, payload = {}) {
    return this.sendProposalWithPayload(proposalId, payload);
  }

  async getPublicProposal(token) {
    await wait(80);
    return ensureSeedProposal();
  }

  async recordProposalResponse(proposalId, payload) {
    await wait(120);
    const p = STATE.proposals.find((x) => x.id === proposalId) || ensureSeedProposal();
    p.status = payload.status; // 'accepted' | 'declined' | 'changes_requested'
    p.accepted_at = payload.status === 'accepted' ? NOW() : null;
    return p;
  }

  async createProposalRevision(proposalId, detail = null) {
    await wait(120);
    const p = STATE.proposals.find((x) => x.id === proposalId) || ensureSeedProposal();
    p.version = (p.version || 1) + 1;
    p.status = 'draft';
    return p;
  }

  // Agreements + billing
  async getAgreementForProposal(proposalId) {
    await wait(60);
    return STATE.agreements[proposalId] || null;
  }

  async createAgreementForProposal(proposalId) {
    await wait(120);
    const agreement = seedAgreement('not_started');
    agreement.proposal_id = proposalId;
    STATE.agreements[proposalId] = agreement;
    return agreement;
  }

  async getWorkspaceAgreement(workspaceId) {
    await wait(60);
    const list = Object.values(STATE.agreements);
    return list.length ? list[list.length - 1] : null;
  }

  async getAgreement(agreementId) {
    await wait(60);
    const list = Object.values(STATE.agreements);
    return list.find((a) => a.id === agreementId) || null;
  }

  async startBilling(agreementId, payload = {}) {
    await wait(160);
    const list = Object.values(STATE.agreements);
    const a = list.find((x) => x.id === agreementId) || seedAgreement('billing_link_sent');
    a.payment_method_status = 'billing_link_sent';
    a.billing_status = 'billing_link_sent';
    a.billing_link = `https://cornercast.tv/pay/${PROPOSAL_ID}`;
    // In demo/preseed mode, the buyer "pays" out-of-band as soon as the
    // billing link is sent. Without this, /launch would block on
    // billing_unconfirmed for the seller-flow demo capture.
    try {
      if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_PRESEED_LATE === 'true') {
        a.payment_method_status = 'payment_confirmed';
        a.billing_status = 'payment_confirmed';
        a.payment_method_label = 'Visa •••• 4242';
        a.status = 'active';
      }
    } catch (_) {}
    STATE.agreements[a.proposal_id] = a;
    return a;
  }

  async confirmBilling(agreementId, payload = {}) {
    await wait(200);
    const list = Object.values(STATE.agreements);
    const a = list.find((x) => x.id === agreementId) || seedAgreement('payment_confirmed');
    a.payment_method_status = 'payment_confirmed';
    a.billing_status = 'payment_confirmed';
    a.payment_method_label = 'Visa •••• 4242';
    STATE.agreements[a.proposal_id] = a;
    return a;
  }

  async failBilling(agreementId, payload = {}) {
    await wait(120);
    const list = Object.values(STATE.agreements);
    const a = list.find((x) => x.id === agreementId);
    if (a) {
      a.payment_method_status = 'payment_failed';
      a.billing_status = 'payment_failed';
    }
    return a;
  }

  async cancelBilling(agreementId, payload = {}) {
    await wait(120);
    const list = Object.values(STATE.agreements);
    const a = list.find((x) => x.id === agreementId);
    if (a) {
      a.payment_method_status = 'cancelled';
      a.billing_status = 'cancelled';
    }
    return a;
  }

  // Launch readiness + campaign
  // Pre-launch state must reflect what the user has actually done in the demo:
  // proposal accepted → billing confirmed → creative selected (we treat the
  // seed proposal's selected_creative_binding_id as proof of selection).
  async getLaunchReadiness(workspaceId) {
    await wait(100);
    const proposal = STATE.proposals[0];
    const agreement = Object.values(STATE.agreements)[0];
    const hasProposal = Boolean(proposal && proposal.status === 'accepted');
    const billingConfirmed = Boolean(agreement && agreement.payment_method_status === 'payment_confirmed');
    const creativeSelected = Boolean(
      STATE.selectedBindingByWorkspace[WORKSPACE_ID]
      || proposal?.selected_creative_binding_id
      || proposal?.creative_binding_id,
    );
    const ready = hasProposal && billingConfirmed && creativeSelected;
    return seedLaunchReadiness({ ready, hasProposal, billingConfirmed, creativeSelected });
  }

  async launchCampaign(workspaceId, payload = {}) {
    await wait(220);
    STATE.campaigns[WORKSPACE_ID] = seedCampaign();
    STATE.reportSummaries[WORKSPACE_ID] = seedReportSummary();
    STATE.snapshots[CAMPAIGN_ID] = seedSnapshot();
    return STATE.campaigns[WORKSPACE_ID];
  }

  // The campaign endpoints return null until launchCampaign() has been called.
  // This keeps the BusinessWorkspace "Campaigns" tab at 0 initially.
  async getWorkspaceCampaign(workspaceId) {
    await wait(80);
    return STATE.campaigns[WORKSPACE_ID] || null;
  }

  async getCampaign(campaignId) {
    await wait(80);
    return STATE.campaigns[WORKSPACE_ID] || null;
  }

  async getWorkspaceCampaignReportSummary(workspaceId) {
    await wait(80);
    return STATE.reportSummaries[WORKSPACE_ID] || null;
  }

  async getCampaignReportSummary(campaignId) {
    await wait(80);
    return STATE.reportSummaries[WORKSPACE_ID] || null;
  }

  async getLatestReportSnapshot(campaignId) {
    await wait(80);
    return STATE.snapshots[campaignId] || null;
  }

  async listReportSnapshots(campaignId) {
    await wait(80);
    return { items: STATE.snapshots[campaignId] ? [STATE.snapshots[campaignId]] : [] };
  }
}

export const mockSellerFlowApiClient = new MockSellerFlowApiClient();
export default mockSellerFlowApiClient;

// Run the optional late-scene preseed once the module is fully evaluated.
maybePreseedLateState();
